import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  Column,
  ColumnInstance,
  IdType,
  PluginHook,
  Row,
  UseTableOptions,
  useColumnOrder,
  useTable,
} from "react-table"
import { LocationSearch, useLocationSearch } from "hooks/useLocationSearch"
import TableRow, { TableRowProps } from "@material-ui/core/TableRow"
import { css, cx } from "@emotion/css"

import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown"
import Button from "ui/buttons/Button"
import { CheckList } from "components/inputs/CheckList"
import { Columns } from "./Columns"
import { EmptyBody } from "./Empty"
import { Filters } from "./filters"
import { HeaderCell } from "./cells"
import { Loading } from "components/Loading"
import MUITable from "@material-ui/core/Table"
import { PAGE_SIZE } from "api/utils"
import { Search } from "./Search"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableContainer from "@material-ui/core/TableContainer"
import TableHead from "@material-ui/core/TableHead"
import { TableLoading } from "./TableLoading"
import TablePagination from "@material-ui/core/TablePagination"
import { TableToolbar } from "./TableToolbar"
import { debounce } from "lodash"
import symmetricDifference from "lodash/fp/symmetricDifference"
import { useI18n } from "providers/I18n"
import { useLocalStorage } from "hooks/useLocalStorage"

const styles = {
  container: css`
    min-width: 100%;
    max-width: 100%;
  `,
  tableContainer: css`
    overflow-y: auto;
    max-height: calc(100vh - 250px);
    ::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 6px;
    }
  `,
  row: css`
    height: var(--spacing-xxl-5);
  `,
  rowExpanded: css`
    background-color: var(--lighter-grey);
    height: var(--spacing-xxl-5);
  `,
  rowNested: css`
    background-color: var(--light-grey-2);
    cursor: default;
    > td {
      border: none;
      padding: 7px var(--spacing-s);
    }
  `,
  rowClickable: css`
    cursor: pointer;
  `,
  commonCell: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 3;
    vertical-align: baseline;
  `,
  cell: css`
    padding: var(--spacing-xs) var(--spacing-s);
  `,
  headerCell: css`
    padding: var(--spacing-xxs) var(--spacing-xs) var(--spacing-xxs)
      var(--spacing-s);
  `,
  nowrap: css`
    white-space: nowrap;
  `,
  sortable: css`
    cursor: pointer;
  `,
  alignRight: css`
    > div {
      justify-content: flex-end;
    }
  `,
  inline: css`
    display: flex;
    align-items: center;
  `,
  ascending: css`
    transform: rotate(180deg);
  `,
  descending: css`
    width: var(--spacing-xl);
    height: var(--spacing-xl);
    color: var(--grey);
  `,
  activeColumn: css`
    color: var(--dark-grey);
  `,
  toolbar: css`
    padding: 0 0 var(--spacing-m);
  `,
  headerExpandable: css`
    &:first-of-type {
      padding-left: 38px;
    }
  `,
}

const eo = {}
const defaultGetRowProps = () => eo

const defaultColumn = {
  Header: HeaderCell,
}

export interface TableFilter {
  name: string
  label: string
  options: Array<{
    value: string
    label: string | ReactNode
    searchLabel?: string
  }>
  hasTranslation?: boolean
  translationPrefix?: string
}

export type TableProps<T extends object = {}> = {
  id: string
  columns: Array<Column<T>>
  data: any
  title?: string
  loading?: boolean
  total: number
  tableHeight?: string
  //
  filters: TableFilter[]
  searchBy?: string
  actionButton?: ReactNode
  //
  plugins?: PluginHook<T>[]
  getSubRows?: UseTableOptions<T>["getSubRows"]
  noResults?: ReactNode

  getRowProps?: (row: Row<T>) => TableRowProps
  hasColumnsReordering?: boolean
  onOpen?: VoidFunction
  createButtonLabel?: string
  isPaginated?: boolean
  localSearch?: ReactNode
  filterTitle?: string
  customFilters?: ReactNode
  autoResetExpanded?: boolean
  className?: string
  initialRowsPerPage?: number
}

export function Table<T extends object = {}>(props: TableProps<T>) {
  const {
    id,
    columns,
    plugins = [],
    getSubRows,
    //
    data,
    //
    total,
    loading,
    searchBy,
    filters,
    filterTitle,
    actionButton,
    //
    noResults,
    //
    getRowProps = defaultGetRowProps,
    hasColumnsReordering = true,
    onOpen,
    createButtonLabel,
    isPaginated = true,
    localSearch,
    customFilters,
    autoResetExpanded = true,
    className,
    tableHeight,
    initialRowsPerPage,
  } = props

  const hiddenColumns = useMemo(
    () =>
      (columns as Array<ColumnInstance<T> & { default: boolean }>)
        .filter((h) => !h.default)
        .map((h) => h.id),
    [columns],
  )

  const {
    getTableProps,
    getTableBodyProps,
    rows,
    headers,
    prepareRow,
    setColumnOrder,
    setHiddenColumns,
    // state,
  } = useTable<T>(
    {
      columns,
      data,
      getSubRows,
      defaultColumn,
      initialState: {
        hiddenColumns,
      },
      autoResetExpanded,
    },
    useColumnOrder,
    ...plugins,
  )

  const { t } = useI18n()

  const [selectedSortField, setSelectedSortField] = useState("")
  const [selectedSortOrder, setSelectedSortOrder] = useState<"DESC" | "ASC">(
    "DESC",
  )

  const ls = useLocationSearch()
  const [storedOrder, setStoredOrder] = useLocalStorage(`${id}-order`, null)
  const [storedHidden, setStoredHidden] = useLocalStorage(`${id}-hidden`, null)
  const searchParam = !!searchBy ? ls.get(searchBy) : undefined
  const [searchTerm, setSearchTerm] = useState<string>(() =>
    !!searchBy ? searchParam || "" : "",
  )

  useEffect(() => {
    if (hasColumnsReordering) {
      if (storedHidden && storedHidden.length > 0) {
        setHiddenColumns(storedHidden)
      } else {
        setStoredHidden(hiddenColumns)
      }
      return () => {
        setStoredHidden(headers.filter((h) => !h.isVisible).map((h) => h.id))
      }
    } else {
      setHiddenColumns([])
    }
    // eslint-disable-next-line
  }, [hiddenColumns, hasColumnsReordering]) // only load from storage on mount // sure

  useEffect(() => {
    if (hasColumnsReordering) {
      if (storedOrder !== null) {
        setColumnOrder(storedOrder)
      }
    }
  }, [setColumnOrder, storedOrder, hasColumnsReordering])

  useEffect(() => {
    if (!searchParam) {
      setSearchTerm("")
    }
  }, [searchParam])

  const handleHeaderClick = (headerId: string, label?: string) => {
    if (selectedSortField === headerId) {
      const newOrder = selectedSortOrder === "DESC" ? "ASC" : "DESC"
      ls.set("sort", `${headerId},${newOrder}`)
      setSelectedSortOrder(newOrder)
    } else {
      ls.set("sort", `${headerId},DESC`)
      setSelectedSortField(headerId)
      setSelectedSortOrder("DESC")
    }
  }

  const handleColumnChange = (columnOrder: Array<IdType<T>>) => {
    setColumnOrder(columnOrder)
    setStoredOrder(columnOrder)
  }

  const handleVisibilityChange = (changedValue: Array<IdType<T>>) => {
    setStoredHidden(symmetricDifference(storedHidden, [changedValue]))
  }

  const handleChangePage = (_evt: any, newPage: number) => {
    ls.set("page", String(newPage))
  }

  const handleChangeRowsPerPage = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    ls.set("page", 0)
    ls.set("pageSize", String(evt.target.value))
  }

  const page = ls.getNumber("page", 0)
  const pageSize = ls.getNumber("pageSize", initialRowsPerPage ?? PAGE_SIZE)

  const debouncedSearch = useRef(
    debounce((value: string, _ls: LocationSearch) => {
      if (searchBy) {
        _ls.set(searchBy, value)
        _ls.set("page", 0)
      }
    }, 200),
  ).current

  const handleSearchTerm = (_: string, value: any) => {
    setSearchTerm(value)
    debouncedSearch(value, ls)
  }

  const handleFiltersReset = () => {
    filters
      .map((flt) => flt.name)
      .concat(["page"])
      .forEach((k) => {
        ls.set(k, "")
      })
  }
  const activeFiltersCount = ls.countAll(filters.map((flt) => flt.name))
  const isExpandableHeader = rows?.some((r) => r?.canExpand)
  const displayToolbar =
    !!localSearch ||
    searchBy ||
    !!filters.length ||
    customFilters ||
    actionButton ||
    createButtonLabel

  return loading && !searchBy ? (
    <Loading />
  ) : (
    <div className={cx(styles.container, className)}>
      {displayToolbar ? (
        <div className={styles.toolbar}>
          <TableToolbar>
            <div className="left">
              {!!localSearch ? localSearch : null}{" "}
              {searchBy ? (
                <Search
                  name={searchBy}
                  value={searchTerm}
                  setValue={handleSearchTerm}
                />
              ) : null}
              {filters.length > 0 ? (
                <Filters
                  count={activeFiltersCount}
                  onReset={handleFiltersReset}
                  title={filterTitle}
                >
                  {filters.map((fltr) => {
                    const totalChecked = fltr.options
                      .map((option) => ls.contains(fltr.name, option.value))
                      .filter(Boolean).length
                    const options = fltr.options.map((option, index) => {
                      const checked = ls.contains(
                        fltr.name,
                        option.value?.trim(),
                      )
                      return {
                        ...option,
                        checked,
                        setValue: (name: string, value: any) => {
                          ls.append(name, value)
                          ls.set("page", 0)
                        },
                      }
                    })

                    const label = t(fltr.label)
                    return (
                      <CheckList
                        key={fltr.name}
                        name={fltr.name}
                        label={label}
                        options={options}
                        totalChecked={totalChecked}
                      />
                    )
                  })}
                </Filters>
              ) : null}
              {customFilters}
            </div>
            {hasColumnsReordering ? (
              <div className="right">
                <Columns<T>
                  data={
                    headers as Array<
                      ColumnInstance<T> & { header: string; fixed: boolean }
                    >
                  }
                  onOrderChange={handleColumnChange}
                  onHiddenChange={handleVisibilityChange}
                />
                {actionButton}
              </div>
            ) : (
              <div className="right">{actionButton}</div>
            )}
            {onOpen ? (
              <div className="right">
                <Button variant="contained" color="primary" onClick={onOpen}>
                  {createButtonLabel}
                </Button>
              </div>
            ) : null}
          </TableToolbar>
        </div>
      ) : null}

      {loading ? (
        <div>
          <TableLoading />
        </div>
      ) : (
        <TableContainer
          className={cx(
            styles.tableContainer,
            css(`height: ${tableHeight || "auto"}`),
          )}
        >
          <MUITable {...getTableProps()} size="small" stickyHeader>
            {total ? (
              <TableHead>
                <TableRow>
                  {headers.map((column) => {
                    return column.isVisible ? (
                      <TableCell
                        {...column.getHeaderProps()}
                        align={column.align}
                        className={cx(
                          "BodyBody-2Dark-greyRegular",
                          styles.nowrap,
                          styles.commonCell,
                          styles.headerCell,
                          {
                            [styles.sortable]: column.sortable,
                            [styles.alignRight]: column.align === "right",
                            [styles.headerExpandable]: isExpandableHeader,
                          },
                        )}
                        onClick={() => {
                          if (column.sortable)
                            handleHeaderClick(column.id, column.header)
                        }}
                      >
                        <div
                          className={
                            column.sortable ? styles.inline : undefined
                          }
                        >
                          {column.render("Header")}
                          {column.sortable ? (
                            <ArrowDropDownIcon
                              className={cx(styles.descending, {
                                [styles.ascending]:
                                  selectedSortOrder === "ASC" &&
                                  selectedSortField === column.id,
                                [styles.activeColumn]:
                                  selectedSortField === column.id,
                              })}
                            />
                          ) : null}
                        </div>
                      </TableCell>
                    ) : null
                  })}
                </TableRow>
              </TableHead>
            ) : null}
            {total ? (
              <TableBody {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row)
                  const rowProps = getRowProps(row)
                  const cn = cx(styles.row, {
                    [styles.rowExpanded]: !!row.isExpanded,
                    [styles.rowNested]: row.depth >= 1,
                    [styles.rowClickable]:
                      !!rowProps && !!Object.keys(rowProps).length,
                  })
                  return (
                    <TableRow
                      data-testid={`table-row-${i + 1}`}
                      {...row.getRowProps()}
                      className={cn}
                      {...rowProps}
                    >
                      {row.cells.map((cell, i) => (
                        <TableCell
                          data-testid={`table-cell-${i + 1}`}
                          {...cell.getCellProps()}
                          align={cell.column.align}
                          width={cell.column.width}
                          classes={{
                            root: cx(
                              "BodyBody-2BlackRegular",
                              styles.commonCell,
                              styles.cell,
                            ),
                          }}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            ) : (
              <EmptyBody>{noResults}</EmptyBody>
            )}
          </MUITable>
          {isPaginated && total ? (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={pageSize}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              classes={{ caption: "BodyBody-sDark-greyRegular" }}
            />
          ) : null}
        </TableContainer>
      )}
    </div>
  )
}
